import { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthProvider"
import {
  ExclamationTriangleIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  BriefcaseIcon,
  CurrencyEuroIcon,
  ChatBubbleBottomCenterTextIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  XMarkIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";

export default function ProspectForm({ open, onClose, onSave, prospect }) {
  const [step, setStep] = useState(1);
  const { user } = useAuth();
  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });
  const [initialType, setInitialType] = useState("Personne Physique");
  const [formData, setFormData] = useState({
    type: "Personne Physique",
    nom: "",
    prenom: "",
    telephone: "",
    email: "",
    linkedin: "",
    premierContact: "",
    profession: "",
    situationMatrimoniale: "",
    adresse: "",
    ville: "",
    codePostal: "",
    enveloppe: "",
    besoins: "",
    commentaire: "",
    referent: user?.Nom || "Non assigné",
    statutJuridique: "",
    siret: "",
    secteurActivite: "",
    statut: "Prospect",
  });

  useEffect(() => {
    if (prospect) {
      setFormData({
        ...prospect,
        _id: prospect._id,
        statut: "Prospect",
        premierContact: prospect.premierContact?.split("T")[0] || "",
        referent: prospect.referent || user?.Nom || "Non assigné",
      });
      setInitialType(prospect.type || "Personne Physique");
    } else if (user) {
      setFormData((prev) => ({
        ...prev,
        referent: user.Nom || "Non assigné",
      }));
      setInitialType("Personne Physique");
    }
  }, [prospect, user]);  

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const showToast = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 3000);
  };

  const handleSubmit = async () => {
    if (!formData.nom.trim()) {
      showToast("Le champ 'Nom' est obligatoire.", "error");
      return;
    }
  
    if (!formData.type) {
      showToast("Le champ 'Type' doit être sélectionné.", "error");
      return;
    }
  
    if (formData.type === "Personne Physique" && !formData.prenom.trim()) {
      showToast("Veuillez indiquer un prénom pour une personne physique.", "error");
      return;
    }
  
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      showToast("L'adresse email semble invalide.", "error");
      return;
    }
  
    if (formData.telephone && formData.telephone.length < 6) {
      showToast("Le numéro de téléphone est trop court.", "error");
      return;
    }
  
    try {

      if (!formData.referent || formData.referent === "Non assigné") {
        formData.referent = user?.Nom || "Non assigné";
      }

      await onSave(formData);
      showToast(
        prospect ? "Prospect modifié avec succès." : "Nouveau prospect ajouté avec succès.",
        "success"
      );
      onClose();
    } catch (err) {
      console.error("❌ Erreur lors de la sauvegarde du prospect :", err);
      showToast("Une erreur est survenue lors de l'enregistrement.", "error");
    }
  };  

  const steps = initialType === "Personne Morale"
  ? [
      { label: "Identité", icon: <UserIcon className="w-5 h-5 inline mr-2" /> },
      { label: "Coordonnées", icon: <PhoneIcon className="w-5 h-5 inline mr-2" /> },
      { label: "Adresse", icon: <MapPinIcon className="w-5 h-5 inline mr-2" /> },
      { label: "Entreprise", icon: <BuildingOffice2Icon className="w-5 h-5 inline mr-2" /> },
      { label: "Besoins", icon: <ChatBubbleBottomCenterTextIcon className="w-5 h-5 inline mr-2" /> },
      { label: "Autres infos", icon: <CalendarDaysIcon className="w-5 h-5 inline mr-2" /> },
    ]
  : [
      { label: "Identité", icon: <UserIcon className="w-5 h-5 inline mr-2" /> },
      { label: "Coordonnées", icon: <PhoneIcon className="w-5 h-5 inline mr-2" /> },
      { label: "Adresse", icon: <MapPinIcon className="w-5 h-5 inline mr-2" /> },
      { label: "Situation", icon: <BriefcaseIcon className="w-5 h-5 inline mr-2" /> },
      { label: "Besoins", icon: <ChatBubbleBottomCenterTextIcon className="w-5 h-5 inline mr-2" /> },
      { label: "Autres infos", icon: <CalendarDaysIcon className="w-5 h-5 inline mr-2" /> },
    ];

    const getStepFields = () => {
      const isPM = initialType === "Personne Morale";
    
      switch (step) {
        case 1:
          return (
            <>
              <div>
                <label className="text-sm font-semibold">Type *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={(e) => {
                    handleChange(e);
                    // si l'utilisateur change le type au step 1, on met aussi à jour initialType
                    setInitialType(e.target.value);
                  }}
                  className="w-full border rounded px-3 py-2 mt-1"
                >
                  <option value="Personne Physique">Personne Physique</option>
                  <option value="Personne Morale">Personne Morale</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold">
                  {isPM ? "Dénomination Sociale *" : "Nom *"}
                </label>
                <input
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-semibold">
                  {isPM ? "Nom Commercial" : "Prénom"}
                </label>
                <input
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
            </>
          );
    
        case 2:
          return (
            <>
              <div>
                <label className="text-sm font-semibold">Téléphone</label>
                <input
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-semibold">Email</label>
                <input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-semibold">LinkedIn</label>
                <input
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
            </>
          );
    
        case 3:
          return (
            <>
              <div>
                <label className="text-sm font-semibold">Adresse</label>
                <input
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-semibold">Ville</label>
                <input
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-semibold">Code Postal</label>
                <input
                  name="codePostal"
                  value={formData.codePostal}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
            </>
          );
    
        case 4:
          if (isPM) {
            return (
              <>
                <div>
                  <label className="text-sm font-semibold">Statut Juridique</label>
                  <input
                    name="statutJuridique"
                    value={formData.statutJuridique}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">SIRET</label>
                  <input
                    name="siret"
                    value={formData.siret}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">Secteur d’activité</label>
                  <input
                    name="secteurActivite"
                    value={formData.secteurActivite}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 mt-1"
                  />
                </div>
              </>
            );
          } else {
            return (
              <>
                <div>
                  <label className="text-sm font-semibold">Profession</label>
                  <input
                    name="profession"
                    value={formData.profession}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">Situation Matrimoniale</label>
                  <input
                    name="situationMatrimoniale"
                    value={formData.situationMatrimoniale}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 mt-1"
                  />
                </div>
              </>
            );
          }
    
        case 5:
          return (
            <>
              <div>
                <label className="text-sm font-semibold">Besoins</label>
                <textarea
                  name="besoins"
                  value={formData.besoins}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-semibold">Enveloppe (€)</label>
                <input
                  name="enveloppe"
                  value={formData.enveloppe}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-semibold">Commentaire</label>
                <textarea
                  name="commentaire"
                  value={formData.commentaire}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
            </>
          );
    
        case 6:
          return (
            <div>
              <label className="text-sm font-semibold">Date du 1er contact</label>
              <input
                type="date"
                name="premierContact"
                value={formData.premierContact}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>
          );
    
        default:
          return null;
      }
    };        

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="relative bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800">
            <XMarkIcon className="w-6 h-6" />
          </button>

          <DialogTitle className="text-xl font-bold text-center mb-4 text-gray-800">
            {steps[step - 1].icon}
            Étape {step} : {steps[step - 1].label}
          </DialogTitle>

          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
            <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${(step / steps.length) * 100}%` }} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{getStepFields()}</div>

          <div className="flex justify-between mt-6">
            <button disabled={step === 1} onClick={() => setStep((s) => s - 1)} className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg">
              Précédent
            </button>
            {step < steps.length ? (
              <button onClick={() => setStep((s) => s + 1)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">
                Suivant
              </button>
            ) : (
              <button onClick={handleSubmit} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg">
                Enregistrer
              </button>
            )}
          </div>
        </DialogPanel>
      </div>

      {/* ✅ Notifications animées */}
      <AnimatePresence>
        {notification.show && (
          <motion.div
            initial={{ opacity: 0, x: 100, y: 50 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 100, y: 50 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-6 right-6 z-50 w-full max-w-sm"
          >
            <div className={`rounded-lg shadow-lg p-4 flex items-start gap-3 border-l-4 ${notification.type === "success" ? "bg-green-50 border-green-500" : "bg-red-50 border-red-500"}`}>
              <div className="shrink-0">
                {notification.type === "success" ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                ) : (
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
                )}
              </div>
              <div className="flex-1 text-sm text-gray-700">
                <p className="font-semibold">{notification.type === "success" ? "Succès" : "Erreur"}</p>
                <p className="text-sm">{notification.message}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Dialog>
  );
}